/* eslint-env browser */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, CircularProgress, FormControlLabel, Grid, TextField, makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import toast from 'react-hot-toast';
import officeInfoService from '../../../../services/OfficeInfoService';
import useFetchProtegeEmployeeDetail from '../../hooks/useFetchProtegeEmployeeDetail';
import useFetchEmployeesPinCode from '../../hooks/useFetchEmployeesPinCode';
import { getPinCodeValidationError } from '../../../MyProfile/helper/helper';
import { extractCardDetails } from '../../helper/helper';
import { Label } from '../../../ui/label';
import prepareQualificationUpload from '../../../Qualification/helper/prepareQualificationUpload';
import { buildAccessLevelPayload, buildOfficeSecurityPayload } from './userOfficeInfoPayload';

const useStyles = makeStyles(() => ({
  textfieldLabel: {
    marginBottom: '5px',
    fontSize: '13px'
  },
  preview: {
    width: 96,
    height: 96,
    borderRadius: '50%',
    objectFit: 'cover',
    border: '1px solid #d8d8d8'
  },
  faceWarning: {
    margin: 0,
    padding: '10px 12px',
    border: '1px solid #ffe082',
    borderRadius: 4,
    backgroundColor: '#fff8e1',
    color: '#795b00',
    fontSize: 13,
    lineHeight: 1.5
  }
}));

const emptyOfficeInfo = {
  officePinCode: '',
  facialRecognitionEnabled: false,
  faceImage: null,
  faceImageUrl: '',
  faceRegistrationPending: false,
  useAsProfilePicture: false,
  accessLevels: []
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;

const UserOfficeInfo = ({ employeeId }) => {
  const classes = useStyles();
  const facePhotoInput = useRef(null);
  const [editButtonClicked, setEditButtonClicked] = useState(false);
  const [officeInfoLoading, setOfficeInfoLoading] = useState(true);
  const [accessLevelsLoading, setAccessLevelsLoading] = useState(true);
  const [accessLevelOptions, setAccessLevelOptions] = useState([]);
  const [savedOfficePinCode, setSavedOfficePinCode] = useState('');
  const [localPreviewUrl, setLocalPreviewUrl] = useState('');

  const { protegeEmployeeDetail, protegeEmployeeLoading } = useFetchProtegeEmployeeDetail(employeeId);
  const { employeesPinCode, employeesPinCodeError, employeesPinCodeLoading } = useFetchEmployeesPinCode();

  const {
    control,
    getValues,
    handleSubmit,
    clearErrors,
    reset,
    resetField,
    setError,
    setValue,
    watch,
    formState: { dirtyFields, errors, isDirty, isSubmitting }
  } = useForm({ defaultValues: emptyOfficeInfo });

  const facialRecognitionEnabled = watch('facialRecognitionEnabled');
  const faceImage = watch('faceImage');
  const faceImageUrl = watch('faceImageUrl');
  const faceRegistrationPending = watch('faceRegistrationPending');
  const facePreviewUrl = useMemo(() => localPreviewUrl || faceImageUrl || '', [faceImageUrl, localPreviewUrl]);
  const cardDetails = extractCardDetails(protegeEmployeeDetail);
  const isFobEnabled = cardDetails.length > 0;
  const facePhotoInputId = `admin-face-image-${employeeId}`;
  const isLoading = officeInfoLoading || accessLevelsLoading || protegeEmployeeLoading || employeesPinCodeLoading;

  useEffect(() => {
    let active = true;

    const fetchOfficeInfo = async () => {
      setOfficeInfoLoading(true);
      try {
        const response = await officeInfoService.getOfficeInfo({ employeeId });
        if (!active) return;

        const data = response?.data?.data || {};
        const values = {
          ...getValues(),
          officePinCode: data.officePinCode == null ? '' : String(data.officePinCode),
          facialRecognitionEnabled: Boolean(data.facialRecognitionEnabled),
          faceImage: null,
          faceImageUrl: data.faceImageUrl || '',
          faceRegistrationPending: Boolean(data.faceRegistrationPending),
          useAsProfilePicture: Boolean(data.useAsProfilePicture)
        };

        setSavedOfficePinCode(values.officePinCode);
        setLocalPreviewUrl('');
        reset(values);
      } catch (error) {
        if (active) toast.error(getErrorMessage(error, 'Could not show office info'));
      } finally {
        if (active) setOfficeInfoLoading(false);
      }
    };

    if (employeeId) fetchOfficeInfo();
    else {
      setSavedOfficePinCode('');
      reset(emptyOfficeInfo);
      setOfficeInfoLoading(false);
    }

    return () => {
      active = false;
    };
  }, [employeeId, getValues, reset]);

  useEffect(() => {
    let active = true;

    const fetchOfficeAccessLevels = async () => {
      setAccessLevelsLoading(true);
      try {
        const response = await officeInfoService.officeAccessLevels();
        if (!active) return;

        const records = response?.data?.data?.data || {};
        setAccessLevelOptions(
          Object.keys(records).map((key) => ({
            value: String(key).trim(),
            label: records[key]
          }))
        );
      } catch (error) {
        if (active) toast.error(getErrorMessage(error, 'Could not load office access levels'));
      } finally {
        if (active) setAccessLevelsLoading(false);
      }
    };

    fetchOfficeAccessLevels();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (accessLevelsLoading || protegeEmployeeLoading) return;

    const employeeAccessLevels = Object.keys(protegeEmployeeDetail || {})
      .filter((key) => /^AccessLevel\d+$/i.test(key))
      .map((key) => String(protegeEmployeeDetail[key] || '').trim())
      .filter((value) => value && value !== '0');
    const matchedAccessLevels = accessLevelOptions.filter((option) => employeeAccessLevels.includes(option.value));

    reset({ ...getValues(), accessLevels: matchedAccessLevels });
  }, [accessLevelOptions, accessLevelsLoading, getValues, protegeEmployeeDetail, protegeEmployeeLoading, reset]);

  useEffect(
    () => () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    },
    [localPreviewUrl]
  );

  const validatePin = (value) => {
    if (String(value) === String(savedOfficePinCode)) return true;
    if (employeesPinCodeLoading) return 'PIN availability is still loading. Please try again.';
    if (employeesPinCodeError) return 'PIN availability could not be checked. Please try again.';
    return getPinCodeValidationError(String(value), employeesPinCode, savedOfficePinCode) || true;
  };

  const clearSelectedFaceImage = () => {
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    setLocalPreviewUrl('');
    setValue('faceImage', null, { shouldDirty: true });
    if (facePhotoInput.current) facePhotoInput.current.value = '';
  };

  const handleFaceImageChange = async (event) => {
    if (!editButtonClicked || !facialRecognitionEnabled) return;
    const selectedFile = event.target.files?.[0] || null;
    if (!selectedFile) return;

    const isImage =
      selectedFile.type.startsWith('image/') ||
      /\.(avif|bmp|gif|hei[cf]|jpe?g|png|tiff?|webp)$/i.test(selectedFile.name || '');
    if (!isImage) {
      setError('faceImage', { type: 'validate', message: 'Select a valid image file.' });
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('faceImage', { type: 'validate', message: 'Image must be 10 MB or smaller.' });
      return;
    }

    try {
      const file = await prepareQualificationUpload(selectedFile);
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(URL.createObjectURL(file));
      clearErrors('faceImage');
      setValue('faceImage', file, { shouldDirty: true, shouldValidate: true });
    } catch (error) {
      setError('faceImage', {
        type: 'validate',
        message: error?.message || 'The selected image could not be converted.'
      });
    }
  };

  const handleFacialRecognitionChange = (field, enabled) => {
    field.onChange(enabled);
    clearErrors('faceImage');
    if (enabled) return;

    clearSelectedFaceImage();
    setValue('useAsProfilePicture', false, { shouldDirty: true });
  };

  const handleCancel = () => {
    if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    setLocalPreviewUrl('');
    clearErrors();
    reset();
    setEditButtonClicked(false);
    if (facePhotoInput.current) facePhotoInput.current.value = '';
  };

  const onSubmit = async (values) => {
    const accessLevelPayload = buildAccessLevelPayload(employeeId, values, dirtyFields);
    const officeSecurityPayload = buildOfficeSecurityPayload(employeeId, values, dirtyFields);

    if (!accessLevelPayload && !officeSecurityPayload) return;
    if (officeSecurityPayload && values.facialRecognitionEnabled && !values.faceImage && !values.faceImageUrl) {
      setError('faceImage', { type: 'required', message: 'Upload a photo before enabling facial recognition.' });
      return;
    }

    let accessLevelsSaved = false;
    try {
      if (accessLevelPayload) {
        await officeInfoService.updateUserOfficeAccessLevel(accessLevelPayload);
        resetField('accessLevels', { defaultValue: values.accessLevels || [] });
        accessLevelsSaved = true;
      }

      if (officeSecurityPayload) {
        const response = await officeInfoService.updateOfficeInfo(officeSecurityPayload);
        const data = response?.data?.data || {};
        const updatedValues = {
          ...getValues(),
          officePinCode:
            data.officePinCode == null ? String(values.officePinCode || '') : String(data.officePinCode || ''),
          facialRecognitionEnabled:
            data.facialRecognitionEnabled == null
              ? values.facialRecognitionEnabled
              : Boolean(data.facialRecognitionEnabled),
          faceImage: null,
          faceImageUrl: data.faceImageUrl || values.faceImageUrl || '',
          faceRegistrationPending:
            data.faceRegistrationPending == null
              ? values.faceRegistrationPending
              : Boolean(data.faceRegistrationPending),
          useAsProfilePicture:
            data.useAsProfilePicture == null ? values.useAsProfilePicture : Boolean(data.useAsProfilePicture),
          accessLevels: values.accessLevels || []
        };

        if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
        setLocalPreviewUrl('');
        setSavedOfficePinCode(updatedValues.officePinCode);
        reset(updatedValues);
        if (facePhotoInput.current) facePhotoInput.current.value = '';
      } else {
        reset({ ...getValues(), accessLevels: values.accessLevels || [] });
      }

      setEditButtonClicked(false);
      if (accessLevelPayload && officeSecurityPayload) toast.success('Office information updated successfully');
      else if (accessLevelPayload) toast.success("Successfully updated user's office access level");
      else toast.success('Office security settings updated successfully');
    } catch (error) {
      const fallback = accessLevelsSaved
        ? 'Access levels were updated, but office security settings could not be updated'
        : 'Office information could not be updated';
      toast.error(getErrorMessage(error, fallback));
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
      <p className="tw-text-[12px]">
        ICT data is refreshed every 4 minutes and Akuvox data every 6 minutes. Saving a PIN writes it immediately to ICT
        and Verd.
      </p>

      <Grid container spacing={3} className="tw-flex tw-items-center">
        <Grid item xs={12} sm={3}>
          <p className={classes.textfieldLabel}>Office PIN</p>
          {officeInfoLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Controller
              name="officePinCode"
              control={control}
              rules={{ required: 'Office PIN is required.', validate: validatePin }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  variant="outlined"
                  size="small"
                  type="text"
                  inputProps={{ inputMode: 'numeric', maxLength: 4, pattern: '[0-9]*' }}
                  fullWidth
                  disabled={!editButtonClicked}
                  error={Boolean(errors.officePinCode)}
                  helperText={errors.officePinCode?.message}
                  onChange={(event) => field.onChange(event.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12} sm={3}>
          <Controller
            name="facialRecognitionEnabled"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(event) => handleFacialRecognitionChange(field, event.target.checked)}
                    disabled={!editButtonClicked}
                    color="primary"
                  />
                }
                label="Facial recognition"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <input
            ref={facePhotoInput}
            id={facePhotoInputId}
            type="file"
            accept="image/*,.heic,.heif"
            hidden
            disabled={!editButtonClicked || !facialRecognitionEnabled}
            onChange={handleFaceImageChange}
          />
          <label htmlFor={facePhotoInputId}>
            <Button component="span" variant="outlined" disabled={!editButtonClicked || !facialRecognitionEnabled}>
              {faceImage || faceImageUrl ? 'Change face photo' : 'Upload face photo'}
            </Button>
          </label>
          {errors.faceImage && <p style={{ color: 'red', fontSize: 12 }}>{errors.faceImage.message}</p>}
        </Grid>

        {facePreviewUrl && (
          <Grid item xs={12} sm={3}>
            <img className={classes.preview} src={facePreviewUrl} alt="Face recognition preview" />
          </Grid>
        )}

        {facialRecognitionEnabled && faceImageUrl && !faceImage && faceRegistrationPending && (
          <Grid item xs={12}>
            <p role="alert" className={classes.faceWarning}>
              Photo uploaded, but Akuvox has not registered the face. If this remains after a few minutes, upload a new
              clear, well-lit, front-facing portrait of one person looking directly at the camera. Keep the entire face
              visible with space around it, and avoid blur, glare, strong backlighting, hats, masks, or side angles.
            </p>
          </Grid>
        )}

        {facialRecognitionEnabled && (faceImage || faceImageUrl) && (
          <Grid item xs={12}>
            <Controller
              name="useAsProfilePicture"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      disabled={!editButtonClicked}
                      color="primary"
                    />
                  }
                  label="Use this image as the user's Verd profile picture"
                />
              )}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={4}>
          <p className={classes.textfieldLabel}>Office Access Level</p>
          {accessLevelsLoading || protegeEmployeeLoading ? (
            <CircularProgress size={20} />
          ) : (
            <Controller
              name="accessLevels"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  value={Array.isArray(field.value) ? field.value : []}
                  isMulti
                  name="accessLevels"
                  options={accessLevelOptions}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  isDisabled={!editButtonClicked}
                />
              )}
            />
          )}
        </Grid>

        {isFobEnabled && (
          <Grid item xs={12} sm={6}>
            <div className="tw-md:tw-col-span-1">
              <Label className="tw-text-sm">FOB Details</Label>
              <div className="tw-px-4 tw-rounded-md tw-bg-white/5 tw-border tw-border-white/10">
                {cardDetails.map((card) => (
                  <div key={card.index} className="tw-grid tw-grid-cols-2 tw-gap-6">
                    <div className="tw-space-y-1">
                      <Label className="tw-text-gray-400">Facility / Card Number</Label>
                    </div>
                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                      <p className="tw-text-sm tw-font-medium tw-border-b-2">{card.facilityCode}</p>
                      <p className="tw-text-sm tw-font-medium tw-border-b-2">{card.cardNumber}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Grid>
        )}

        <Grid item xs={12}>
          {editButtonClicked ? (
            <>
              <Button
                type="button"
                onClick={handleCancel}
                variant="outlined"
                color="secondary"
                disabled={isSubmitting}
                style={{ marginRight: 10 }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="outlined" color="primary" disabled={isSubmitting || !isDirty}>
                {isSubmitting && <CircularProgress size={20} style={{ marginRight: 10 }} />}
                Save
              </Button>
            </>
          ) : (
            <Button
              type="button"
              onClick={() => setEditButtonClicked(true)}
              variant="outlined"
              color="primary"
              disabled={isLoading || isSubmitting}
            >
              Edit
            </Button>
          )}
        </Grid>
      </Grid>
    </form>
  );
};

UserOfficeInfo.propTypes = {
  employeeId: PropTypes.any
};

export default UserOfficeInfo;
